<script setup>
// İade/Değişim ortak sipariş+kalem seçici: müşteri ara → sipariş kartı →
// içinden ürün satırlarına tıkla (çoklu seçim, tek sipariş kuralı).
// Hem İade/Değişim panelinde hem konuşma içi popup'ta kullanılır.
import { ref, computed, watch, onMounted } from 'vue';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  initialQuery: { type: String, default: '' },
});

const emit = defineEmits(['selection']);

const lookupQ = ref(props.initialQuery);
const lookupOrders = ref([]);
const lookupLoading = ref(false);
const lookupError = ref('');
const selectedOrder = ref(null);
const selectedIdx = ref([]);

const fmtTl = v =>
  v == null
    ? '—'
    : `${new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 }).format(v)} TL`;

const runLookup = async () => {
  if (lookupQ.value.trim().length < 3) return;
  lookupLoading.value = true;
  lookupError.value = '';
  try {
    const data = await fetchMarketplaceJson(
      props.accountId,
      `returns/lookup?q=${encodeURIComponent(lookupQ.value.trim())}`
    );
    lookupOrders.value = data.orders || [];
    if (!lookupOrders.value.length)
      lookupError.value = 'Bu aramayla sipariş bulunamadı.';
  } catch (e) {
    lookupError.value = e.message;
  } finally {
    lookupLoading.value = false;
  }
};

const selectOrder = o => {
  if (selectedOrder.value?.id !== o.id) {
    selectedOrder.value = o;
    selectedIdx.value = [];
  }
};

const toggleItem = (o, idx) => {
  selectOrder(o);
  const pos = selectedIdx.value.indexOf(idx);
  if (pos >= 0) selectedIdx.value.splice(pos, 1);
  else selectedIdx.value.push(idx);
};

const isItemSelected = (o, idx) =>
  selectedOrder.value?.id === o.id && selectedIdx.value.includes(idx);

const selectedItems = computed(() =>
  selectedOrder.value
    ? selectedIdx.value.map(i => selectedOrder.value.items[i]).filter(Boolean)
    : []
);

const selectedTotal = computed(() =>
  selectedItems.value.reduce((sum, it) => sum + (it.total || 0), 0)
);

watch([selectedOrder, selectedItems], () => {
  emit('selection', {
    order: selectedOrder.value,
    items: selectedItems.value,
    total: selectedTotal.value,
  });
});

const reset = () => {
  lookupQ.value = '';
  lookupOrders.value = [];
  selectedOrder.value = null;
  selectedIdx.value = [];
  lookupError.value = '';
};

defineExpose({ reset, runLookup });

onMounted(() => {
  if (props.initialQuery && props.initialQuery.trim().length >= 3) runLookup();
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex gap-2 flex-wrap items-center">
      <input
        v-model="lookupQ"
        type="text"
        placeholder="Sipariş bul: telefon, sipariş no veya isim"
        class="flex-1 min-w-56 rounded-lg border border-n-strong bg-n-solid-1 px-4 py-2.5 text-base text-n-slate-12"
        @keyup.enter="runLookup"
      />
      <button
        class="px-4 py-2.5 rounded-lg bg-n-solid-3 text-n-slate-12 text-base font-semibold hover:bg-n-solid-1"
        :disabled="lookupLoading"
        @click="runLookup"
      >
        {{ lookupLoading ? 'Aranıyor…' : 'Siparişi Ara' }}
      </button>
    </div>
    <div v-if="lookupError" class="text-sm text-ruby-700 font-medium">
      {{ lookupError }}
    </div>

    <div v-if="lookupOrders.length" class="flex flex-col gap-3">
      <div
        v-for="o in lookupOrders"
        :key="o.id"
        class="rounded-xl border-2 overflow-hidden"
        :class="
          selectedOrder?.id === o.id ? 'border-woot-600' : 'border-n-weak'
        "
      >
        <!-- sipariş başlık şeridi -->
        <button
          class="w-full text-left px-4 py-2.5 flex flex-wrap gap-x-4 gap-y-1 items-center text-base bg-n-alpha-black2"
          @click="selectOrder(o)"
        >
          <span
            class="px-2 py-0.5 rounded-full text-xs font-bold"
            :class="
              o.channel === 'ticimax'
                ? 'bg-violet-100 text-violet-800'
                : 'bg-green-100 text-green-800'
            "
          >
            {{ o.channel === 'ticimax' ? 'Koza' : 'ModaX' }}
          </span>
          <span class="font-bold text-n-slate-12">#{{ o.orderNumber }}</span>
          <span class="text-n-slate-11">{{ o.orderedAt }}</span>
          <span class="font-semibold text-n-slate-12">{{
            o.customerName
          }}</span>
          <span class="text-n-slate-11">{{ fmtTl(o.amount) }}</span>
          <span
            v-if="o.cargoDeliveredOn"
            class="px-2 py-0.5 rounded bg-green-100 text-green-900 text-xs font-semibold"
          >
            Teslim: {{ o.cargoDeliveredOn }}
          </span>
          <span
            v-if="o.existingReturnCode"
            class="px-2 py-0.5 rounded bg-yellow-100 text-yellow-900 text-xs font-bold"
          >
            Zaten kayıtlı: {{ o.existingReturnCode }}
          </span>
          <span
            v-if="selectedOrder?.id === o.id"
            class="ml-auto text-sm font-bold text-woot-600"
          >
            Seçili — iade edilecek ürünlere tıkla
          </span>
        </button>
        <!-- kalemler: kompakt satırlar -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2 p-3">
          <button
            v-for="(it, idx) in o.items"
            :key="idx"
            class="flex items-center gap-3 rounded-lg border px-2.5 py-2 text-left"
            :class="
              isItemSelected(o, idx)
                ? 'border-woot-600 bg-woot-25'
                : 'border-n-weak bg-n-solid-1 hover:border-woot-400'
            "
            @click="toggleItem(o, idx)"
          >
            <img
              v-if="it.image"
              :src="it.image"
              class="w-11 h-14 rounded-md object-cover shrink-0 border border-n-weak"
              loading="lazy"
            />
            <div
              v-else
              class="w-11 h-14 rounded-md bg-n-alpha-black2 shrink-0 flex items-center justify-center text-lg"
            >
              👗
            </div>
            <div class="min-w-0 flex-1">
              <div class="text-sm font-semibold text-n-slate-12 truncate">
                {{ it.title || it.code }}
              </div>
              <div class="text-xs text-n-slate-11 truncate">
                <span class="font-semibold">{{ it.variant }}</span>
                <span v-if="it.title" class="font-mono ml-1">{{
                  it.code
                }}</span>
              </div>
              <div class="text-xs text-n-slate-11">
                {{ it.quantity }} adet · {{ fmtTl(it.total) }}
              </div>
            </div>
            <span
              class="w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-sm font-bold border-2"
              :class="
                isItemSelected(o, idx)
                  ? 'bg-woot-600 border-woot-600 text-white'
                  : 'border-n-strong text-transparent'
              "
            >
              ✓
            </span>
          </button>
          <div
            v-if="!o.items?.length"
            class="text-sm text-n-slate-11 px-2 py-4"
          >
            Bu siparişte ürün detayı yok — talebi ürün alanını elle doldurarak
            açabilirsiniz.
          </div>
        </div>
      </div>
    </div>

    <!-- seçim özeti -->
    <div
      v-if="selectedItems.length"
      class="rounded-xl bg-woot-25 border border-woot-200 px-4 py-2.5 flex flex-wrap items-center gap-2"
    >
      <span class="text-base font-bold text-woot-700">
        Seçilen: {{ selectedItems.length }} ürün — {{ fmtTl(selectedTotal) }}
      </span>
      <span
        v-for="(it, i) in selectedItems"
        :key="i"
        class="px-2 py-1 rounded-lg bg-n-solid-1 border border-n-weak text-sm text-n-slate-12"
      >
        {{ it.code }} {{ it.variant }}
      </span>
    </div>
  </div>
</template>
